/**
 * Loadable wrapper.
 * Allows for a container to display a spinner AND set an accessible status via aria-busy.
 */

import { Box, type BoxProps, Chip, CircularProgress, styled } from '@mui/material';

const StyledChip = styled(Chip)`
    background-color: ${({ theme }) => theme.palette.primary.main};
    color: white;
`;

type LoadingOverlayProps = {
    fullScreen?: boolean;
};

const LoadingOverlay = styled(Box, {
    shouldForwardProp: (prop: any) => !['fullScreen'].includes(prop)
})<LoadingOverlayProps>`
    position: ${({ fullScreen }) => (fullScreen ? 'fixed' : 'absolute')};
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    z-index: 1000;
    background: rgba(255, 255, 255, ${({ fullScreen }) => (fullScreen ? 0.5 : 0)});
    top: 0;
    left: 0;
` as React.FC<BoxProps & LoadingOverlayProps>;

type LoadableContainerProps = {
    isLoading?: boolean;
    hideContentOnLoading?: boolean;
};

const LoadableContainer = styled(Box, {
    shouldForwardProp: (prop: any) => !['isLoading', 'hideContentOnLoading'].includes(prop)
})<LoadableContainerProps>`
    position: relative;

    ${({ isLoading, hideContentOnLoading }) => {
        if (!isLoading) {
            return;
        }

        return `
            & > *:not(.loading-overlay) {
                opacity: ${hideContentOnLoading ? 0 : 0.5};
                transition: opacity 0.3s ease-in-out;
                * {
                    opacity: ${hideContentOnLoading ? 0 : 0.5};
                    transition: opacity 0.3s ease-in-out;
                }
            }
        `;
    }}
` as React.FC<BoxProps & LoadableContainerProps>;

type LoadableProps = {
    hideContentOnLoading?: boolean;
    fullScreen?: boolean;
    ariaLive?: 'polite' | 'off' | 'assertive' | undefined;
    isLoading?: boolean;
    children?: any;
    title?: string;
    testId?: string;
    className?: string;
};

const Loadable = (props: LoadableProps) => {
    const {
        hideContentOnLoading,
        fullScreen = false,
        ariaLive = 'polite',
        children,
        isLoading,
        title,
        testId = 'loading-overlay',
        className
    } = props;

    return (
        <LoadableContainer
            isLoading={isLoading}
            hideContentOnLoading={hideContentOnLoading}
            aria-live={ariaLive}
            aria-busy={isLoading}
            className={className}
        >
            {isLoading && (
                <LoadingOverlay fullScreen={fullScreen} className="loading-overlay" data-testid={testId}>
                    {title && <StyledChip label={title} />}
                    <CircularProgress disableShrink />
                </LoadingOverlay>
            )}

            {children}
        </LoadableContainer>
    );
};

export default Loadable;
